import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'srcReact/components/common2/Modal'
import styles from './styles.module.scss'
import Button from 'srcReact/components/common2/Button'
import { useSelector, useDispatch } from 'react-redux'
import { commonActions } from 'srcReact/components'
import useInfinitScroll from 'srcReact/helpers/hooks/useInfinitScroll'
import { settingsActions } from 'srcReact/Settings/action'
import { getCurrentOrgId } from 'srcReact/helpers/organizationHelper'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'
import Invite from './components/Invite'
import SelectGroup from './components/SelectGroup'
import { organizationSettingsActions } from 'srcReact/OrganizationSettingsPage'
import Spinner from 'srcReact/components/common2/Spinner'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import config from 'config'
import { loginActions } from 'srcReact/LoginPage'

function TransferRoleModal({ showModal, hideModal, screen, type = 'deactivate', userToDelete }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [screenType, setScreenType] = useState(screen)
  const [selectedUser, setSelectedUser] = useState(0)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [search, setSearch] = useState('')
  const [dataToTransfer, setDataToTransfer] = useState(new Map())
  const [clientError, setClientError] = useState('')
  const [deactivateSuccess, setDeactivateSuccess] = useState(false)

  const [pagination, setPagination] = useState({
    nextOffset: 0,
    isPagination: false
  })

  const {
    organization: { organization_list },
    settings: {
      deactivateAccData: { data: deactivatedResult, loading: deactivateLoading, error: deactivateError }
    },
    common: {
      transferRole: {
        loading,
        error,
        data,
        users: { list, stats }
      }
    },
    organizationSettings: {
      deleteAccData: { data: deletedData }
    }
  } = useSelector((state) => state)

  const lastMemberRef = useInfinitScroll(loading, stats && stats.remaining, stats && stats.next_offset, setPagination, [
    loading
  ])

  useEffect(() => {
    if (deactivateSuccess) {
      if (organization_list && organization_list.items && organization_list.items.length === 1) {
        dispatch(loginActions.userLogout())
      } else {
        window.location = `${config.webUrl}/0/${appConstantSettings.DEFAULT_HOMEPAGE}`
      }
    }
  }, [deactivateSuccess])

  useEffect(() => {
    if (deletedData && deletedData.deleted === 1) {
      hideModal()
    }
  }, [deletedData])

  useEffect(() => {
    if (deactivatedResult) {
      if (deactivatedResult.deactivated === 1) {
        setDeactivateSuccess(true)
      }
    }
  }, [deactivatedResult])

  useEffect(() => {
    if (pagination.isPagination || search) {
      dispatch(commonActions.getMembersToTransferGroup(selectedGroup.id, pagination, search))
    }
  }, [pagination])

  useEffect(() => {
    if (data && data.groups_to_transfer && data.groups_to_transfer.length === 1) {
      const group = data.groups_to_transfer[0]
      selectMembersForGroup(group.id, group)
    }
  }, [data])

  const handleSearch = (searchValue) => {
    if (searchValue.length >= 2 || searchValue.length === 0) {
      setPagination({
        nextOffset: 0,
        isPagination: false
      })
      setSearch(searchValue.toLowerCase())
    }
  }

  const selectMembersForGroup = (id, selectedGroup = null) => {
    let group = selectedGroup
    if (!group) {
      group = data.groups_to_transfer.find((x) => x.id === id)
    }
    setSelectedGroup(group)
    dispatch(commonActions.getMembersToTransferGroup(id, pagination, ''))
    setScreenType('select-user')
  }

  const selectUser = (user) => {
    setSelectedUser(user)
  }

  const handleCancel = () => {
    if (screenType === 'select-group') {
      hideModal()
    } else {
      setScreenType('select-group')
    }
  }

  const handleTransferRole = () => {
    if (dataToTransfer.size !== data.groups_to_transfer.length) {
      setClientError(t('transferRole.error1'))
    } else {
      let tranferPayload = [...dataToTransfer.entries()].map(([groupId, value], index) => {
        return `${groupId}:${value.user.id}`
      })
      tranferPayload = tranferPayload.join('|')
      deactivateOrDeleteUser(tranferPayload)
    }
  }

  const handleInvite = () => {
    if (data && data.groups_to_transfer && data.groups_to_transfer.length === 1) {
      let tranferPayload = `${selectedGroup.id}:${selectedUser.id}`
      deactivateOrDeleteUser(tranferPayload)
    } else {
      setDataToTransfer((map) => new Map(map.set(selectedGroup.id, { group: selectedGroup, user: selectedUser })))
      setScreenType('select-group')
    }
  }

  const deactivateOrDeleteUser = (tranferPayload) => {
    const orgId = getCurrentOrgId()

    if (type === 'deactivate') {
      dispatch(settingsActions.deactivateAccount(orgId, tranferPayload))
    } else if (type === 'delete') {
      const userId = userToDelete.id || userToDelete.resourceKey
      dispatch(organizationSettingsActions.deleteAccount(orgId, userId, tranferPayload))
    }
  }

  return (
    <Modal
      customContentStyle={{
        width: '27rem',
        height: '40rem',
        minHeight: '38rem',
        maxHeight: '45rem',
        justifyContent: 'flex-start',
        paddingBottom: '1.5rem'
      }}
      isOpen={showModal}
      handleClose={hideModal}
      title={screenType === 'select-group' ? t('transferRole.title') : t('transferRole.title2')}
      subTitle={screenType === 'select-group' ? t('transferRole.subTitle') : t('transferRole.subTitle2')}>
      {(error || deactivateError) && (
        <ErrorMessage text={error || deactivateError} center={true} className={styles.error} />
      )}
      {clientError && <ErrorMessage text={clientError} center={true} className={styles.error} />}
      <div className={styles.transferRoleContainer}>
        {screenType === 'select-user' && (
          <Invite
            selectedGroup={selectedGroup}
            selectedUser={selectedUser}
            handleSearch={handleSearch}
            selectUser={selectUser}
            members={list}
            lastMemberRef={lastMemberRef}
            loading={loading}
          />
        )}
        {screenType === 'select-group' && (
          <SelectGroup dataToTransfer={dataToTransfer} groups={data} selectMembersForGroup={selectMembersForGroup} />
        )}
        {deactivateLoading && <Spinner />}
        <div className={styles.buttons}>
          <Button
            style='secondary'
            handleClick={handleCancel}
            text={screenType === 'select-group' ? t('global.cancel') : t('global.back')}
          />
          <Button
            handleClick={screenType === 'select-group' ? handleTransferRole : handleInvite}
            text={screenType === 'select-group' ? t('global.done') : t('global.invite')}
          />
        </div>
      </div>
    </Modal>
  )
}

export default TransferRoleModal
